import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { playerLogin, clearError } from '../../features/auth/authSlice';

const inputClass =
  'w-full px-5 py-4 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition text-base';

export const IdentityVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { teamId, playerName, planUid: selectedPlanUid } = location.state || {};
  const { loading, error } = useSelector((state) => state.auth);

  // Use player's name as suggested X username
  const suggestedXUsername = playerName ? playerName.replace(/\s+/g, '') : '';

  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isCorrectXUsername, setIsCorrectXUsername] = useState(null); // null = not answered, true = yes, false = no
  const [newXURL, setNewXURL] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    // Clear error when component mounts
    dispatch(clearError());

    // Clear error when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!teamId || !playerName) {
      alert('Missing team or player information. Please start from the login page.');
      navigate('/auth/waiting-verification');
      return;
    }

    const formData = new FormData();
    formData.append('teamId', teamId);
    formData.append('playerName', playerName);
    formData.append('email', email);
    // Player plan for Outseta payment flow — chosen on the plan-selection step
    // (falls back to the default player plan if none was selected)
    formData.append('planUid', selectedPlanUid || 'nmDA4wQy');
    if (file) {
      formData.append('photoIdDocument', file);
    }

    // Send X URL - either confirmed player name or new one
    if (isCorrectXUsername === true && suggestedXUsername) {
      formData.append('xURL', `https://x.com/${suggestedXUsername}`);
    } else if (isCorrectXUsername === false && newXURL) {
      formData.append('xURL', `https://x.com/${newXURL}`);
    }

    const resultAction = await dispatch(playerLogin(formData));

    if (playerLogin.fulfilled.match(resultAction)) {
      // Pending payment: redirect to Outseta checkout (same as signup flow)
      if (resultAction.payload.outsetaCheckoutUrl) {
        // Keep a full-screen loader up until the browser navigates to Outseta
        setRedirecting(true);
        window.location.href = resultAction.payload.outsetaCheckoutUrl;
        return;
      }
      navigate('/auth/profile-approved');
    }
  };

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Full-screen loader while verifying / redirecting to Outseta checkout */}
      {(redirecting || loading) && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-jp-page/95 backdrop-blur-sm px-6 text-center" role="status">
          <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-jp-accent mb-8"></div>
          <p className="text-jp-ink text-3xl md:text-4xl font-anton tracking-wide">
            {redirecting ? 'TAKING YOU TO SECURE CHECKOUT' : 'VERIFYING YOUR IDENTITY'}
          </p>
          <p className="text-jp-muted text-base md:text-lg mt-4 max-w-xl">
            {redirecting
              ? 'Hang tight — we’re redirecting you to our secure payment partner, Outseta, to complete your subscription.'
              : 'Please wait while we confirm your details.'}
          </p>
          <p className="text-jp-muted/80 text-sm mt-5">
            This only takes a moment. Please don’t close or refresh this window.
          </p>
        </div>
      )}

      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            Step 3
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            IDENTITY VERIFICATION
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Confirm that it's you.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="rounded-xl p-6 sm:p-10 bg-jp-section border border-jp-outline/50 space-y-6">

            {/* Error Message */}
            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="verify-email" className="block text-base font-semibold text-jp-ink mb-2">
                Email
              </label>
              <input
                id="verify-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="alex.chase@school.edu"
                className={inputClass}
              />
            </div>

            {/* X Username Verification */}
            {suggestedXUsername && (
              <div className="space-y-4">
                <div>
                  <span className="block text-base font-semibold text-jp-ink mb-2">
                    Is this your X username?
                  </span>
                  <div className="flex items-center gap-3 px-5 py-4 rounded-xl bg-jp-field border border-jp-line">
                    <svg className="w-5 h-5 text-jp-ink flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className="text-jp-accent font-semibold text-lg">@{suggestedXUsername}</span>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    aria-pressed={isCorrectXUsername === true}
                    onClick={() => setIsCorrectXUsername(true)}
                    className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent ${
                      isCorrectXUsername === true
                        ? 'bg-jp-accent/10 border-jp-accent text-jp-accent'
                        : 'bg-jp-field border-jp-line text-jp-ink hover:border-jp-accent/60'
                    }`}
                  >
                    Yes, this is mine
                  </button>
                  <button
                    type="button"
                    aria-pressed={isCorrectXUsername === false}
                    onClick={() => setIsCorrectXUsername(false)}
                    className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent ${
                      isCorrectXUsername === false
                        ? 'bg-red-500/10 border-red-500 text-red-600 dark:text-red-400'
                        : 'bg-jp-field border-jp-line text-jp-ink hover:border-jp-accent/60'
                    }`}
                  >
                    No, it's not mine
                  </button>
                </div>

                {isCorrectXUsername === false && (
                  <div>
                    <label htmlFor="verify-x-username" className="block text-base font-semibold text-jp-ink mb-2">
                      Your correct X username
                    </label>
                    <div className="flex items-center w-full rounded-xl bg-jp-field border border-jp-line focus-within:ring-2 focus-within:ring-jp-accent/40 focus-within:border-jp-accent transition overflow-hidden">
                      <span className="pl-5 py-4 text-jp-muted text-base whitespace-nowrap select-none">x.com/</span>
                      <input
                        id="verify-x-username"
                        type="text"
                        value={newXURL}
                        onChange={(e) => setNewXURL(e.target.value)}
                        placeholder="username"
                        className="flex-1 px-1 py-4 bg-transparent text-jp-ink placeholder-jp-muted/70 focus:outline-none text-base"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* File Upload */}
            <div>
              <label htmlFor="verify-photo-id" className="block text-base font-semibold text-jp-ink mb-2">
                Photo ID
              </label>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative w-full px-5 py-6 rounded-xl bg-jp-field border-2 border-dashed transition cursor-pointer focus-within:ring-2 focus-within:ring-jp-accent/40 ${
                  isDragging
                    ? 'border-jp-accent bg-jp-accent/5'
                    : 'border-jp-line hover:border-jp-accent/60'
                }`}
              >
                <input
                  id="verify-photo-id"
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                {file ? (
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xl font-bold">
                        {file.name.endsWith('.pdf') ? 'PDF' : '📷'}
                      </span>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-jp-ink text-sm font-semibold truncate">{file.name}</p>
                      <p className="text-jp-muted text-xs mt-1">
                        {(file.size / 1024).toFixed(1)} KB · click to change
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 text-jp-muted">
                    <div className="w-16 h-16 rounded-xl bg-jp-accent/10 text-jp-accent flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-jp-ink text-sm font-semibold">Drop your photo here or click to browse</p>
                      <p className="text-xs mt-1">A photo or PDF of your ID</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Next'}
            </button>
          </form>

          <div className="px-2 pt-5 pb-1 text-center text-sm sm:text-base">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="font-semibold text-jp-accent hover:underline focus:outline-none focus-visible:underline"
            >
              Back
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IdentityVerificationPage;
